import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "Cambridge House is a long-time partner with Kitco because of its unparalleled reach. Cambridge House is in the business of connecting people face-to-face. Ideas are better shared when people come together. In the past 25 years that simple philosophy has established Cambridge House at the forefront of the must-attend industry events for both the resource and technology sectors. When Cambridge plans to market a conference, targeting is key. Companies attending our shows want an audience that is receptive to their story. Kitco brings us enthusiastic conference goers. Nobody knows the gold industry like Kitco. It is widely followed and trusted. Digital advertising is moving fast, and we appreciate how Kitco continues to innovate. It is always extending the value of its offerings. We have worked with a lot of industry publications, and from our vantage point Kitco has stood out. Its service offering and reach, 5M unique visitors each month, is unmatched. I recommend Kitco.",
      author: "Steve Davidson",
      title: "Former Vice President of Sales",
      company: "Cambridge House",
      image: "https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/h31nscac_author-steve-davidson.png"
    },
    {
      quote: "While we were establishing Northern Empire, it was crucial that our company's story was widely shared and understood by the retail investor community, and that is why we partnered with Kitco. Northern Empire was an explorer focused on the Sterling Gold project about 185km north of Las Vegas, NV. The company had a successful exit in 2018 when it was acquired by Coeur Mining for Can$117 million. When the company reached a stage where it needed broader awareness from the investor community, Kitco was a natural choice because of Kitco's huge reach and trusted name. Kitco is the go-to news source for all-things gold. Our company needed an introduction to the retail investor community. Kitco worked with us to develop a plan to position Northern Empire's unique value proposition: excellent project location, great jurisdiction and deep management talent. The geology was fantastic, too. Shooting video with Kitco was ideal since our message flowed natively within Kitco's news flow. The video interview with Northern Empire's management delivered a powerful message. Seeing the management up close and on-air made an impact. Viewers could see Northern Empire's talent up close, one of our company's unique selling advantages. I recommend Kitco, and hope to use them on my next venture!",
      author: "Mike Allen",
      title: "Former President, CEO and Director",
      company: "Northern Empire",
      image: "https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/nn41acpt_author-mike-allen.png"
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-yellow-500 mb-4">
            Testimonials
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See what our clients say about working with Kitco
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/60 hover:shadow-[0_0_35px_rgba(244,185,66,0.2)] hover:bg-gray-800/95 transition-all duration-700 group relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-yellow-500/5 before:via-transparent before:to-yellow-500/5 before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-700">
            <CardContent className="p-8 md:p-12">
              <div className="text-center">
                <Quote className="w-12 h-12 text-yellow-500 mx-auto mb-6" />
                
                <blockquote className="text-xl md:text-2xl text-white leading-relaxed mb-8 italic">
                  "{testimonials[currentTestimonial].quote}"
                </blockquote>

                <div className="flex items-center justify-center space-x-4 mb-8">
                  <img 
                    src={testimonials[currentTestimonial].image}
                    alt={testimonials[currentTestimonial].author}
                    className="w-16 h-16 rounded-full object-cover border-2 border-yellow-500"
                  />
                  <div className="text-left">
                    <div className="text-yellow-500 font-bold text-lg">
                      {testimonials[currentTestimonial].author}
                    </div>
                    <div className="text-gray-300">
                      {testimonials[currentTestimonial].title}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {testimonials[currentTestimonial].company}
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-center space-x-4">
                  <Button
                    variant="outline" 
                    size="sm"
                    onClick={prevTestimonial}
                    className="border-yellow-500/40 text-yellow-500 hover:bg-yellow-500/10"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  {/* Dots */}
                  <div className="flex space-x-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === currentTestimonial ? 'bg-yellow-500' : 'bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm" 
                    onClick={nextTestimonial}
                    className="border-yellow-500/40 text-yellow-500 hover:bg-yellow-500/10"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Social Proof */}
        <div className="mt-16 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-3xl font-bold text-yellow-500 mb-2">4.9/5</div>
              <div className="text-white font-semibold">Client Satisfaction</div>
              <div className="text-gray-400 text-sm">Based on 200+ reviews</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-yellow-500 mb-2">250%</div>
              <div className="text-white font-semibold">Average ROI</div>
              <div className="text-gray-400 text-sm">Client advertising campaigns</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-yellow-500 mb-2">24/7</div>
              <div className="text-white font-semibold">Support Available</div>
              <div className="text-gray-400 text-sm">Dedicated account management</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;